/**
 * KestrelGraphSurface — the whole Kestrel graph on one canvas (FXC-10027).
 *
 * The product DAG (React Flow + ELK) is built for walking lineage a few dozen
 * nodes at a time; the Kestrel scale story needs all 654 records and 855 typed
 * edges at once — 478 Spool human records as a starfield above the engineering
 * weave, the 16 story anchors in gold, the invalidated path in red. This is
 * the native V5 port of the deck's dag.html, fed by the committed payload at
 * /api/demo-v3/kestrel/graph (deterministic manifest data, no live DB read).
 */
import { useCallback, useEffect, useRef, useState } from "react";

interface GraphNode {
  id: string;
  type: "version" | "operation" | "decision" | "lifecycle" | "human";
  kind?: string;
  t: string;
  label: string;
  state?: string | null;
  anchor?: boolean;
  channel?: string | null;
}
interface GraphEdge { s: string; d: string; k: string }
interface GraphPayload {
  nodes: GraphNode[];
  edges: GraphEdge[];
  counts: Record<string, number>;
}
interface Placed { n: GraphNode; x: number; y: number }

const T0 = Date.parse("2026-05-03T00:00:00Z");
const T1 = Date.parse("2026-07-08T00:00:00Z");
const PAD_L = 90;
const PAD_R = 40;
const TOP = 24;
const BOT = 48;
const CHANNELS = ["#kx40-aero", "#meshing", "#solver-support", "#program-office",
  "#flight-sciences", "#tooling"];
const GRID_DATES = ["2026-05-04", "2026-05-18", "2026-06-01", "2026-06-15",
  "2026-06-29", "2026-07-06"];

function hash01(s: string): number {
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) { h ^= s.charCodeAt(i); h = Math.imul(h, 16777619); }
  return ((h >>> 0) % 1000) / 1000;
}

function inStory(n: GraphNode): boolean {
  if (n.anchor) return true;
  if (n.type === "version" && n.state && n.state !== "active" && n.state !== "valid") return true;
  if (n.type === "lifecycle" || n.type === "decision") return true;
  if (n.type === "operation" && /kx-b|review|correct|renorm|invalid/.test(n.label)) return true;
  if (n.type === "version" && /kx40-high-lift-review|kx-b/.test(n.label)) return true;
  return false;
}

function nodeStyle(n: GraphNode): { c: string; r: number; glow?: boolean } {
  const bad = n.state && n.state !== "active" && n.state !== "valid";
  if (n.type === "human")
    return n.anchor ? { c: "#ffd166", r: 4.5, glow: true } : { c: "#6a7570", r: 1.6 };
  if (n.type === "operation") return { c: "#8fb8d8", r: 2.6 };
  if (n.type === "version")
    return bad ? { c: "#ff7a7a", r: 4, glow: true } : { c: "#5aa88a", r: 2.6 };
  return { c: "#e8c483", r: 3.4 };
}

export function KestrelGraphSurface() {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const placedRef = useRef<Placed[]>([]);
  const byIdRef = useRef<Record<string, Placed>>({});
  const [data, setData] = useState<GraphPayload | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [storyMode, setStoryMode] = useState(false);
  const [tip, setTip] = useState<{ x: number; y: number; head: string; body: string } | null>(null);

  useEffect(() => {
    fetch("/api/demo-v3/kestrel/graph")
      .then((r) => { if (!r.ok) throw new Error(`graph payload ${r.status}`); return r.json(); })
      .then(setData)
      .catch((e) => setError(String(e)));
  }, []);

  const draw = useCallback(() => {
    const cv = canvasRef.current, wrap = wrapRef.current;
    if (!cv || !wrap || !data) return;
    const W = wrap.clientWidth, H = wrap.clientHeight;
    const dpr = Math.min(2, window.devicePixelRatio || 1);
    cv.width = W * dpr; cv.height = H * dpr;
    cv.style.width = `${W}px`; cv.style.height = `${H}px`;
    const ctx = cv.getContext("2d");
    if (!ctx) return;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, W, H);

    const kinds = Array.from(new Set(
      data.nodes.filter((n) => n.type === "version").map((n) => n.kind ?? "artifact"),
    )).sort();
    const lanes: Array<{ key: string; label: string }> = [
      ...CHANNELS.map((c) => ({ key: `ch:${c}`, label: c })),
      { key: "human:other", label: "meetings · notes" },
      { key: "gap1", label: "" },
      { key: "ops", label: "operation runs" },
      { key: "gap2", label: "" },
      ...kinds.map((k) => ({ key: `v:${k}`, label: k })),
      { key: "events", label: "lifecycle · decisions" },
    ];
    const usable = H - TOP - BOT;
    const laneY: Record<string, number> = {};
    lanes.forEach((l, i) => { laneY[l.key] = TOP + usable * (i + 0.5) / lanes.length; });
    const laneH = usable / lanes.length;
    const yOfLane = (key: string) => laneY[key] ?? TOP + usable / 2;
    const xOf = (t: string) => {
      const f = (Date.parse(t) - T0) / (T1 - T0);
      return PAD_L + Math.max(0, Math.min(1, f)) * (W - PAD_L - PAD_R);
    };

    const placed: Placed[] = data.nodes.map((n) => {
      let key: string;
      if (n.type === "human") {
        key = n.channel && laneY[`ch:${n.channel}`] != null ? `ch:${n.channel}` : "human:other";
      } else if (n.type === "operation") key = "ops";
      else if (n.type === "version") key = `v:${n.kind ?? "artifact"}`;
      else key = "events";
      return { n, x: xOf(n.t), y: yOfLane(key) + (hash01(n.id) - 0.5) * laneH * 0.66 };
    });
    placedRef.current = placed;
    byIdRef.current = Object.fromEntries(placed.map((p) => [p.n.id, p]));

    ctx.font = "11px var(--fc-font-mono, ui-monospace)";
    GRID_DATES.forEach((d) => {
      const f = (Date.parse(d) - T0) / (T1 - T0);
      const x = PAD_L + f * (W - PAD_L - PAD_R);
      ctx.strokeStyle = "rgba(242,244,243,.05)";
      ctx.beginPath(); ctx.moveTo(x, TOP); ctx.lineTo(x, H - BOT + 10); ctx.stroke();
      ctx.fillStyle = "rgba(242,244,243,.35)";
      ctx.fillText(d.slice(5), x - 14, H - BOT + 24);
    });
    lanes.forEach((l) => {
      if (!l.label) return;
      ctx.fillStyle = "rgba(242,244,243,.30)";
      ctx.fillText(l.label, 8, yOfLane(l.key) + 3);
    });

    data.edges.forEach((e) => {
      const a = byIdRef.current[e.s], b = byIdRef.current[e.d];
      if (!a || !b) return;
      const story = inStory(a.n) && inStory(b.n);
      if (storyMode && !story) return;
      let color: string, alpha: number;
      if (e.k === "mention") { color = "255,209,102"; alpha = a.n.anchor ? 0.5 : 0.10; }
      else if (e.k === "lifecycle" || e.k === "cause") { color = "255,122,122"; alpha = 0.65; }
      else if (e.k === "reply") { color = "106,117,112"; alpha = 0.16; }
      else { color = "90,168,138"; alpha = story ? 0.55 : 0.14; }
      ctx.strokeStyle = `rgba(${color},${alpha})`;
      ctx.beginPath();
      ctx.moveTo(a.x, a.y);
      const mx = (a.x + b.x) / 2;
      ctx.bezierCurveTo(mx, a.y, mx, b.y, b.x, b.y);
      ctx.stroke();
    });

    placed.forEach((p) => {
      if (storyMode && !inStory(p.n)) {
        ctx.fillStyle = "rgba(106,117,112,.10)";
        ctx.beginPath(); ctx.arc(p.x, p.y, 1.1, 0, 7); ctx.fill();
        return;
      }
      const s = nodeStyle(p.n);
      if (s.glow) {
        ctx.fillStyle = `${s.c}33`;
        ctx.beginPath(); ctx.arc(p.x, p.y, s.r * 2.6, 0, 7); ctx.fill();
      }
      ctx.fillStyle = s.c;
      ctx.beginPath(); ctx.arc(p.x, p.y, s.r, 0, 7); ctx.fill();
    });
  }, [data, storyMode]);

  useEffect(() => {
    draw();
    window.addEventListener("resize", draw);
    return () => window.removeEventListener("resize", draw);
  }, [draw]);

  const onMove = useCallback((ev: React.MouseEvent<HTMLCanvasElement>) => {
    const rect = ev.currentTarget.getBoundingClientRect();
    const mx = ev.clientX - rect.left, my = ev.clientY - rect.top;
    let best: Placed | null = null, bd = 196;
    for (const p of placedRef.current) {
      const d = (p.x - mx) ** 2 + (p.y - my) ** 2;
      if (d < bd) { bd = d; best = p; }
    }
    if (!best) { setTip(null); return; }
    setTip({
      x: Math.min(rect.width - 380, mx + 14),
      y: my + 14,
      head: `${best.n.type}${best.n.kind ? ` · ${best.n.kind}` : ""}` +
        `${best.n.anchor ? " · STORY ANCHOR" : ""}` +
        `${best.n.state && best.n.state !== "active" ? ` · ${best.n.state}` : ""}` +
        ` · ${(best.n.t || "").slice(0, 16).replace("T", " ")}`,
      body: best.n.label,
    });
  }, []);

  if (error) {
    return (
      <div className="v5-kestrel-graph" data-testid="kestrel-graph-surface">
        <p className="v5-muted">Could not load the Kestrel graph payload: {error}</p>
      </div>
    );
  }
  return (
    <div className="v5-kestrel-graph" data-testid="kestrel-graph-surface">
      <header className="v5-kestrel-graph-head">
        <span className="v5-kestrel-graph-brand">Project Kestrel · the whole graph</span>
        {data ? (
          <span className="v5-kestrel-graph-counts" data-testid="kestrel-graph-counts">
            <b>{data.counts.nodes}</b> records · <b>{data.counts.edges}</b> typed edges ·{" "}
            <b>{data.counts.human}</b> human records · <b>{data.counts.anchors}</b> anchors ·{" "}
            <b>one</b> wrong number
          </span>
        ) : (
          <span className="v5-kestrel-graph-counts">loading…</span>
        )}
        <button
          type="button"
          className="v5-kestrel-graph-toggle"
          onClick={() => setStoryMode((v) => !v)}
          data-testid="kestrel-graph-story-toggle"
        >
          {storyMode ? "Show everything" : "Show the story"}
        </button>
      </header>
      <div className="v5-kestrel-graph-canvas" ref={wrapRef}>
        <canvas ref={canvasRef} onMouseMove={onMove} onMouseLeave={() => setTip(null)} />
        {tip ? (
          <div className="v5-kestrel-graph-tip" style={{ left: tip.x, top: tip.y }}>
            <div className="k">{tip.head}</div>
            <div>{tip.body}</div>
          </div>
        ) : null}
      </div>
      <footer className="v5-kestrel-graph-legend">
        <span><i style={{ background: "#5aa88a" }} />versions</span>
        <span><i style={{ background: "#8fb8d8" }} />operations</span>
        <span><i style={{ background: "#6a7570" }} />chatter</span>
        <span><i style={{ background: "#ffd166" }} />the 16 anchors</span>
        <span><i style={{ background: "#ff7a7a" }} />the wrong number's path</span>
      </footer>
    </div>
  );
}
